import React, { useState } from "react";
import { Stack, Info } from "@phosphor-icons/react";
import { formatRelativeTime } from "@/lib/format";
import { useAnalytics } from "@/components/assets/useAssets";
import { fmtInt, trimPartial } from "@/components/assets/parts";
import { TokenMetaProvider } from "@/components/assets/tokenMeta";
import { AssetSearchBar } from "@/components/assets/AssetSearchBar";
import { AssetMetricCards } from "@/components/assets/AssetMetricCards";
import { ActivityTrend } from "@/components/assets/ActivityTrend";
import { WhatChanged } from "@/components/assets/WhatChanged";
import { TopAssetsTable } from "@/components/assets/TopAssetsTable";
import { Watchlist } from "@/components/assets/Watchlist";
import { PolicyDrawer } from "@/components/assets/PolicyDrawer";
import { AdaMarketCard } from "@/components/AdaPrice";

const ChainTip = ({ stats }) => {
  const block = stats?.as_of?.block_no ?? stats?.freshness?.block_no;
  const time = stats?.freshness?.block_time;
  const stale = stats?.freshness?.stale;
  return (
    <div data-testid="dashboard-chaintip" className="rounded-xl border border-primary/20 bg-card/40 backdrop-blur-md px-4 py-2.5 min-w-[180px]">
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] text-muted-foreground mono">
        chain tip <Info size={11} className="opacity-50" />
      </div>
      <div className={`font-heading text-[16px] leading-tight ${stale ? "text-amber-300" : "text-[#22d3ee]"}`}>
        {time ? formatRelativeTime(time) : "—"}
      </div>
      <div className="text-[10px] mono text-muted-foreground/70">Block {block != null ? fmtInt(block) : "—"}</div>
    </div>
  );
};

export const NativeAssetsPage = ({ onPickAsset, onOpenExplorer }) => {
  const [policyId, setPolicyId] = useState(null);
  const [win, setWin] = useState("24h");
  const openPolicy = (id) => setPolicyId(id);

  const { data: stats, loading: statsLoading } = useAnalytics("/assets/stats", {}, { ttl: 60000, refreshMs: 90000 });
  const { data: chart, loading: chartLoading, error: chartError, reload: chartReload } = useAnalytics(
    "/assets/charts",
    { days: "30" },
    { ttl: 300000, refreshMs: 120000 },
  );
  const daily = trimPartial(chart?.points || []);

  return (
    <TokenMetaProvider>
    <main data-testid="native-assets-page" className="max-w-[1480px] mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
      {/* header */}
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-5 min-w-0">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-primary/70 mono mb-1">
              <Stack size={14} weight="duotone" className="text-primary" /> network overview
            </div>
            <h1 data-testid="dashboard-title" className="font-heading text-2xl sm:text-3xl lg:text-[34px] font-bold tracking-tight text-foreground leading-none">
              Native Assets Analytics
            </h1>
          </div>
          {/* animated chain-activity beam */}
          <div className="hidden lg:flex items-center flex-1 min-w-[120px] max-w-[320px] relative h-px bg-gradient-to-r from-primary/40 via-primary/10 to-transparent">
            <span className="absolute left-0 h-1.5 w-1.5 rounded-full bg-[#22d3ee] shadow-[0_0_10px_2px_rgba(34,211,238,0.7)] tip-beam" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <AdaMarketCard />
          <ChainTip stats={stats} />
        </div>
      </header>

      {/* search + window */}
      <AssetSearchBar onPickAsset={onPickAsset} onOpenPolicy={openPolicy} win={win} setWin={setWin} />

      {/* metric cards */}
      <AssetMetricCards stats={stats} daily={daily} win={win} loading={statsLoading} />

      {/* trend + what changed */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2 min-w-0">
          <ActivityTrend daily={daily} loading={chartLoading} error={chartError} reload={chartReload} freshness={chart?.freshness} />
        </div>
        <div className="min-w-0">
          <WhatChanged daily={daily} freshness={chart?.freshness || stats?.freshness} />
        </div>
      </div>

      {/* top assets + watchlist */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-base md:text-lg font-heading text-foreground/90 inline-flex items-center gap-2"><Stack size={16} className="text-primary" /> Explore assets</h2>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2 min-w-0">
          <TopAssetsTable win={win} onPickAsset={onPickAsset} onOpenPolicy={openPolicy} onOpenExplorer={onOpenExplorer} />
        </div>
        <div className="min-w-0">
          <Watchlist onPickAsset={onPickAsset} />
        </div>
      </div>

      <PolicyDrawer policyId={policyId} onClose={() => setPolicyId(null)} onPickAsset={onPickAsset} />
    </main>
    </TokenMetaProvider>
  );
};
